import {compact} from '@benchling/js-utils';
import React, {useEffect, useCallback, useState, useMemo} from 'react';
import areEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {validateCompleteBenchlingExpression} from 'benchling-expressions/BenchlingExpressionUtils';
import {ProcessFlowchartEdgeConfigSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {setExpressionsAndConfiguredFlagForRouterConfig} from 'processes/graph-builder-processes/flowchart-viz/action-functions/configureRouterProcessFlowchartActionFunction';
import {NodeDetailsSidepanelContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import {createLegacyGraphbuilderNodeFromFlowchartNode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {
  findAllDownstreamMappableNodeIds,
  findAllUpstreamMatchingNodeIds,
  isMappableSourceNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {
  ProcessGraphBuilderNode,
  ProcessGraphBuilderRootNode,
  ProcessGraphBuilderTaskNode,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  CreateOrUpdateRouterFunctionSpec,
  RouterConfig,
} from 'processes/modals/router-config/RouterConfigConstants';
import {useRouterConfigForm, RouterConfigForm} from 'processes/modals/router-config/RouterConfigModal';
import {validateAllFunctionSpecs} from 'processes/modals/router-config/RouterConfigValidation';
import {TransitionSource} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  getInteractiveTaskNodeDatas,
  isInteractiveRouterNode,
  isStaticPlaceholderNodeData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  FlowchartNodeChangeType,
  FlowchartEdgeChangeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {
  getDefaultTransitionSource,
  getTransitionSourceByNodeIdPairKey,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import usePrevious from 'ui-library/hooks/usePrevious';
import {ContextPanelContent} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {ProcessFlowchartEdgeType} from '../processFlowchartConfigEditorConstants';
import {
  RouterNode,
  TaskSchemaNode,
  isInteractivePlaceholderNode,
} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChange} from '../processFlowchartConfigEditorUtils';
import {useFlowchartSidepanelTabUnsavedChanges} from './useFlowchartSidepanelTabUnsavedChanges';

function RouterNodeDetailsSidepanelWithValidRouter(props: {
  selectedNode: RouterNode;
  inputNodes: Array<ProcessGraphBuilderNode>;
  immediateChildNodes: Array<ProcessGraphBuilderNode>;
  transitionSource: TransitionSource;
}): JSX.Element {
  const {
    flowchartTaskSchema,
    allSchemasById,
    applyFlowchartChanges,
    interactiveFlowchart,
    didInitialFlowchartHaveChainedRouters,
  } = NodeDetailsSidepanelContext.useContext();

  const doesCurrentFlowchartHaveChainedRouters = useMemo(() => {
    for (const edge of interactiveFlowchart.edges) {
      if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
        continue;
      }
      const sourceNode = interactiveFlowchart.nodes.find((node) => node.id === edge.source);
      const targetNode = interactiveFlowchart.nodes.find((node) => node.id === edge.target);
      if (
        sourceNode != null &&
        targetNode != null &&
        isInteractiveRouterNode(sourceNode) &&
        isInteractiveRouterNode(targetNode)
      ) {
        return true;
      }
    }
    return false;
  }, [interactiveFlowchart]);

  const {functionSpecs, defaultFunctionSpec, name, submitDisabledMessage, ...routerConfigFormState} =
    useRouterConfigForm({
      inputNodes: props.inputNodes,
      routerConfig: props.selectedNode.data.routerConfig,
      analyticsMetadata: {},
      flowchartSchema: flowchartTaskSchema,
      routerNodeClientId: props.selectedNode.id,
    });

  const [routerConfig, setRouterConfig] = useState({
    clientId: props.selectedNode.data.routerConfig.clientId,
    name,
    defaultFunctionSpec,
    functionSpecs,
  });
  const prevRouterConfig = usePrevious(routerConfig);

  const {setIsTabDirty} = useFlowchartSidepanelTabUnsavedChanges(`router-${props.selectedNode.id}`);

  const getMatchingEdgeId = useCallback(
    (edgeConfigReference: ProcessFlowchartEdgeConfigSpec): string | null => {
      // The router config modal uses the Graphbuilder edge client ID convention so edgeConfigReference.clientId
      // does not actually match an edge in the interactiveFlowchart. So instead we find the edge which matches
      // the from/to node client IDs.
      const matchingEdge = interactiveFlowchart.edges.find(
        (edge) =>
          edge.source === edgeConfigReference.fromNodeClientId &&
          edge.target === edgeConfigReference.toNodeClientId
      );
      return matchingEdge?.id ?? null;
    },
    [interactiveFlowchart.edges]
  );

  useEffect(() => {
    const buildFunctionSpecWithEdgeId = (
      functionSpec: CreateOrUpdateRouterFunctionSpec
    ): CreateOrUpdateRouterFunctionSpec => {
      if (functionSpec.edgeConfigReference == null) {
        return {
          ...functionSpec,
          edgeConfigReference: undefined,
        };
      } else {
        const matchingEdgeId = getMatchingEdgeId(functionSpec.edgeConfigReference);
        if (matchingEdgeId == null) {
          // The edge could not be found, it was likely deleted from the flowchart. Clear the edge reference
          return {
            ...functionSpec,
            edgeConfigReference: undefined,
          };
        }
        return {
          ...functionSpec,
          edgeConfigReference: {
            clientId: matchingEdgeId,
            fromNodeClientId: functionSpec.edgeConfigReference.fromNodeClientId,
            toNodeClientId: functionSpec.edgeConfigReference.toNodeClientId,
          },
        };
      }
    };

    const builtDefaultFunctionSpec = buildFunctionSpecWithEdgeId(defaultFunctionSpec);
    const builtFunctionSpecs = compact(
      functionSpecs.map((functionSpec) => buildFunctionSpecWithEdgeId(functionSpec))
    );

    setRouterConfig({
      clientId: props.selectedNode.data.routerConfig.clientId,
      name,
      defaultFunctionSpec: builtDefaultFunctionSpec,
      functionSpecs: builtFunctionSpecs,
    });
  }, [
    functionSpecs,
    defaultFunctionSpec,
    name,
    props.selectedNode.data.routerConfig.clientId,
    getMatchingEdgeId,
  ]);

  const isFormInvalid = useMemo(
    () =>
      submitDisabledMessage != null ||
      validateAllFunctionSpecs(
        routerConfig.defaultFunctionSpec,
        routerConfig.functionSpecs,
        allSchemasById
      ) != null,
    [submitDisabledMessage, routerConfig.defaultFunctionSpec, routerConfig.functionSpecs, allSchemasById]
  );

  useEffect(() => {
    setIsTabDirty(isFormInvalid);
  }, [isFormInvalid, setIsTabDirty]);

  useEffect(() => {
    // When the router config has changed and is valid we apply the changes to the flowchart.
    if (isFormInvalid) {
      return;
    }

    if (areEqual(prevRouterConfig, routerConfig)) {
      return;
    }

    // Skip applying changes on initial render (when prevRouterConfig is undefined)
    // to avoid marking the form as dirty when opening the sidepanel
    if (prevRouterConfig == null) {
      return;
    }

    let configuredRouterConfig: RouterConfig | null = routerConfig;
    if (
      routerConfig.functionSpecs.every(
        (functionSpec) =>
          functionSpec.expression != null && validateCompleteBenchlingExpression(functionSpec.expression)
      )
    ) {
      configuredRouterConfig = setExpressionsAndConfiguredFlagForRouterConfig(routerConfig);
    }

    // Changes to the router config may change the labels on the edges.
    const edgeLabelChanges: Array<FlowchartEdgeChange> = compact([
      ...routerConfig.functionSpecs.map((functionSpec) => {
        if (functionSpec.edgeConfigReference != null) {
          const edgeId = getMatchingEdgeId(functionSpec.edgeConfigReference);
          return edgeId != null
            ? {
                type: FlowchartEdgeChangeType.LABEL as const,
                id: edgeId,
                label: functionSpec.name,
              }
            : null;
        }
        return null;
      }),
      (() => {
        if (routerConfig?.defaultFunctionSpec?.edgeConfigReference != null) {
          const edgeId = getMatchingEdgeId(routerConfig.defaultFunctionSpec.edgeConfigReference);
          return edgeId != null
            ? {
                type: FlowchartEdgeChangeType.LABEL as const,
                id: edgeId,
                label: routerConfig.defaultFunctionSpec.name,
              }
            : null;
        }
        return null;
      })(),
    ]);
    applyFlowchartChanges((flowchartDraftState) => ({
      nodeChanges: [
        {
          type: FlowchartNodeChangeType.REPLACE,
          id: props.selectedNode.id,
          data: {
            ...props.selectedNode.data,
            nodeName: configuredRouterConfig.name,
            routerConfig: configuredRouterConfig,
          },
        },
      ],
      edgeChanges: edgeLabelChanges,
    }));
  }, [
    prevRouterConfig,
    applyFlowchartChanges,
    routerConfig,
    props.selectedNode.data,
    props.selectedNode.id,
    isFormInvalid,
    getMatchingEdgeId,
    allSchemasById,
  ]);

  const {inputNode} = routerConfigFormState;

  const inputTaskSchema = allSchemasById[
    inputNode.taskSchemaId
  ] as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  assert(inputTaskSchema != null, 'Input task schema not found');
  const inputSchema =
    props.transitionSource === TransitionSource.INPUTS ? inputTaskSchema : inputTaskSchema.outputSchema;
  assert(inputSchema != null, 'Input schema not found');

  return (
    <RouterConfigForm
      {...{...routerConfigFormState, allSchemasById}}
      functionSpecs={functionSpecs}
      defaultFunctionSpec={defaultFunctionSpec}
      name={name}
      submitDisabledMessage={submitDisabledMessage}
      routerNodeClientId={props.selectedNode.id}
      flowchartClientId=""
      analyticsMetadata={{}}
      immediateChildNodes={props.immediateChildNodes}
      didInitialFlowchartHaveChainedRouters={didInitialFlowchartHaveChainedRouters}
      doesCurrentFlowchartHaveChainedRouters={doesCurrentFlowchartHaveChainedRouters}
      flowchartTaskSchema={flowchartTaskSchema}
      inputSchema={inputSchema}
      showDisabledMessageAsBanner={true}
    />
  );
}

export default function RouterNodeDetailsSidepanelContent({
  selectedNode,
}: {
  selectedNode: RouterNode;
}): JSX.Element {
  const {interactiveFlowchart, transitionSourceByNodeIdPair} = NodeDetailsSidepanelContext.useContext();

  const inputFlowchartNodes = useMemo(
    () =>
      findAllUpstreamMatchingNodeIds(
        interactiveFlowchart,
        selectedNode.id,
        (node) => isStaticPlaceholderNodeData(node.data) || isMappableSourceNode(node)
      ).map((nodeId) => {
        const node = interactiveFlowchart.nodes.find((node) => node.id === nodeId);
        assert(node != null, 'Node not found');
        return node;
      }),
    [interactiveFlowchart, selectedNode.id]
  );

  const immediateChildFlowchartNodes = interactiveFlowchart.edges
    .filter((edge) => edge.source === selectedNode.id)
    .map((edge) => {
      const childNode = interactiveFlowchart.nodes.find((node) => node.id === edge.target);
      assert(childNode != null, 'Child node not found');
      return childNode;
    });

  const getErrorMessage = (): string | null => {
    if (inputFlowchartNodes.length === 0) {
      return 'Cannot edit the router configuration because there are no inputs to the router.';
    } else if (inputFlowchartNodes.length > 1) {
      return 'Cannot edit the router configuration because there are multiple inputs to the router.';
    } else if (immediateChildFlowchartNodes.length < 2) {
      return 'Cannot edit the router configuration because there are fewer than 2 outputs of the router.';
    } else if (inputFlowchartNodes.some((node) => isInteractivePlaceholderNode(node))) {
      return 'Cannot edit the router configuration because the input has not been fully configured.';
    } else if (immediateChildFlowchartNodes.some((node) => isInteractivePlaceholderNode(node))) {
      return 'Cannot edit the router configuration because one or more of the downstream nodes have not been fully configured.';
    }
    return null;
  };

  const errorMessage = getErrorMessage();
  if (errorMessage != null) {
    return <ContextPanelContent>{errorMessage}</ContextPanelContent>;
  }

  const inputNodes = inputFlowchartNodes.map(
    (inputNode: TaskSchemaNode) =>
      createLegacyGraphbuilderNodeFromFlowchartNode(inputNode) as
        | ProcessGraphBuilderTaskNode
        | ProcessGraphBuilderRootNode
  );

  const immediateChildNodes = immediateChildFlowchartNodes.map((childNode) =>
    createLegacyGraphbuilderNodeFromFlowchartNode(childNode)
  );

  const inputFlowchartNode = inputFlowchartNodes[0];
  assert(inputFlowchartNode != null, 'Input node not found');
  const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
  const transitionSources = new Set<TransitionSource>();
  for (const nodeId of downstreamMappableNodeIds) {
    const transitionSource =
      transitionSourceByNodeIdPair[getTransitionSourceByNodeIdPairKey(inputFlowchartNode.id, nodeId)] ??
      getDefaultTransitionSource(
        inputFlowchartNode.id,
        getInteractiveTaskNodeDatas(interactiveFlowchart.nodes)
      );
    transitionSources.add(transitionSource);
  }
  if (transitionSources.size > 1) {
    const downstreamNodeNames = downstreamMappableNodeIds
      .map((nodeId) => {
        const node = interactiveFlowchart.nodes.find((node) => node.id === nodeId);
        assert(node != null, 'Node not found');
        return node.data.nodeName;
      })
      .sort((leftName, rightName) => leftName.localeCompare(rightName))
      .join(', ');
    return (
      <ContextPanelContent>
        Cannot edit the router configuration because the the downstream nodes {downstreamNodeNames} map from
        different sources in {inputFlowchartNode.data.nodeName}. All nodes must map from the same data source
        to configure a router.
      </ContextPanelContent>
    );
  }

  const transitionSource = [...transitionSources][0];

  return (
    <RouterNodeDetailsSidepanelWithValidRouter
      selectedNode={selectedNode}
      inputNodes={inputNodes}
      immediateChildNodes={immediateChildNodes}
      transitionSource={transitionSource}
    />
  );
}
